/* one% Shop section: tiles that expand a shared panel in place, plus the product dialog.
   Port of the Component class in "Shop Section.dc.html" (default props). Vanilla, no build step.
   Data: one <script type="application/json" data-op-shop-block> per range block, merged field by field
   over the design fallback (<script data-op-shop-fallback>) for that range. */
(() => {
  if (customElements.get('op-shop')) return;

  const money = (n) => 'Rp ' + new Intl.NumberFormat('id-ID').format(Math.round(n));
  const lc = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const has = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  const pick = (...vals) => { for (const v of vals) if (has(v)) return v; return vals[vals.length - 1]; };
  const readJSON = (el) => { if (!el) return null; try { return JSON.parse(el.textContent); } catch (e) { return null; } };
  const cssUrl = (u) => 'url("' + String(u).replace(/["\\\n]/g, (c) => '\\' + c) + '")';

  function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* Build one range's model: product data first, design fallback per field. */
  function buildModel(block, fb, assetBase) {
    const p = block.product || {};
    const f = fb || {};
    const asset = (s) => (!s ? '' : /^(https?:)?\/\//.test(s) || s.charAt(0) === '/' ? s : assetBase + s);
    const tint = pick(p.tint, f.tint, '#f6f7f7');
    const accent = pick(p.accent, f.accent, '#1a2222');
    const pill = f.pill || [accent, '#fff'];
    const variants = Array.isArray(p.variants) ? p.variants.filter((v) => v && v.id) : [];
    const fbFlav = Array.isArray(f.flavours) ? f.flavours : [];
    const findFb = (name) => fbFlav.find((x) => lc(x.name) === lc(name));

    // Flavours: metafield, else the variants' Flavour option values, else the design list.
    let flav = Array.isArray(p.flavours) && p.flavours.length ? p.flavours : null;
    if (!flav && variants.length) {
      const names = [...new Set(variants.map((v) => v.flavour).filter(Boolean))];
      flav = names.map((name) => {
        const m = findFb(name);
        const v = variants.find((x) => x.flavour === name);
        return { name, image: m ? m.image : v && v.image, colors: m ? m.colors : null };
      });
    }
    if (!flav) flav = fbFlav;
    const flavours = flav.map((x) => {
      const m = findFb(x.name) || {};
      const colors = Array.isArray(x.colors) && x.colors.length >= 4 ? x.colors
        : Array.isArray(m.colors) && m.colors.length >= 4 ? m.colors
        : [tint, accent, pill[0], pill[1]];
      return { name: x.name, img: asset(x.image || m.image), colors };
    });

    // Packs: from variants (price in cents), else the design's packs.
    let packs;
    if (variants.length) {
      const counts = [...new Set(variants.map((v) => Number(v.pack) || 1))].sort((a, b) => a - b);
      packs = counts.map((count) => ({
        count,
        // A flavour that doesn't come in this pack (e.g. the SuperPuffs Mix Flavour is 2 packs only) gets na: its pill hides.
        cells: flavours.map((fl) => {
          const own = variants.filter((x) => lc(x.flavour) === lc(fl.name));
          const v = (own.length ? own : variants).find((x) => (Number(x.pack) || 1) === count);
          return v ? { price: Number(v.price) / 100, id: v.id } : { price: 0, id: null, na: true };
        }),
      }));
    } else {
      packs = (f.packs || [[1, 0]]).map(([count, price]) => ({ count, cells: flavours.map(() => ({ price, id: null })) }));
    }

    // Nutrition label (mirrors `label` in the design).
    const fn = f.nutrition || {};
    const n = p.nutrition && typeof p.nutrition === 'object' ? p.nutrition : fn;
    const nf = Array.isArray(n.flavours) ? n.flavours : [];
    const fnf = Array.isArray(fn.flavours) ? fn.flavours : [];
    const ingMap = p.ingredients && typeof p.ingredients === 'object' ? p.ingredients : {};
    const ingFor = (name) => { for (const k in ingMap) if (lc(k) === lc(name)) return ingMap[k]; return null; };
    const labelFlavours = flavours.map((fl, k) => {
      const own = nf.find((x) => lc(x.name) === lc(fl.name)) || nf[k] || {};
      const back = fnf.find((x) => lc(x.name) === lc(fl.name)) || {};
      return {
        kcal: pick(own.kcal, back.kcal, ''),
        rows: pick(own.rows, back.rows, []),
        ing: pick(own.ingredients, own.ing, ingFor(fl.name), back.ingredients, ''),
      };
    });

    return {
      range: block.range,
      title: pick(p.title, f.title, ''),
      moment: pick(block.moment, p.moment, f.moment, ''),
      tint, accent, pill,
      beat: pick(p.beat, f.beat, ''),
      story: pick(p.story, f.story, ''),
      claims: pick(p.claims, f.claims, []),
      size: pick(p.size, f.size, ''),
      unit: pick(p.unit, f.unit, 'bottle'),
      flavours,
      packs,
      label: {
        mult: Number(pick(n.mult, fn.mult, 1)) || 1,
        override: n.override || {},
        serving: pick(n.serving, p.serving_size, fn.serving, ''),
        what: pick(n.what, fn.what, ''),
        whatHead: pick(n.what_head, n.whatHead, fn.what_head, ''),
        whatBody: pick(n.what_body, n.whatBody, fn.what_body, ''),
        allergens: pick(n.allergens, fn.allergens, ''),
        note: n === fn ? fn.note || '' : n.note || '',
        certs: pick(n.certs, p.certs, fn.certs, []),
        flavours: labelFlavours,
      },
      storage: p.storage || '',
    };
  }

  // Scale a per-serving label value to the pack ("10 g" x 1.8 = "18 g"), as the design's mul().
  function scaler(mult) {
    return (s) => {
      if (!s) return s;
      const m = String(s).match(/^([\d.,]+)\s*(.*)$/);
      if (!m) return s;
      const n = parseFloat(m[1].replace(/,/g, '')) * mult;
      const u = m[2];
      const r = u === 'g' ? Math.round(n * 10) / 10 : Math.round(n);
      return r.toLocaleString('en-US', { maximumFractionDigits: 1 }) + (u === '%' ? '%' : ' ' + u);
    };
  }

  class OpShop extends HTMLElement {
    connectedCallback() {
      if (!this._init) {
        this._init = true;
        this.setup();
      }
      window.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() {
      window.removeEventListener('keydown', this.onKey);
    }

    setup() {
      this.section = this.closest('.op-shop') || this;
      this.reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      this.L = readJSON(this.querySelector('[data-op-shop-labels]')) || {};
      const fb = readJSON(this.querySelector('[data-op-shop-fallback]')) || {};
      const base = this.dataset.assetBase || '';

      this.tiles = [...this.querySelectorAll('.op-shop__tile')];
      this.models = this.tiles.map((t, i) => {
        const block = readJSON(this.querySelector('[data-op-shop-block="' + i + '"]')) || { range: 'water', product: null };
        return buildModel(block, fb[block.range], base);
      });
      this.panel = this.querySelector('[data-panel]');
      this.dialog = this.querySelector('[data-dialog]');
      this.grid = this.querySelector('.op-shop__grid');
      this.state = {
        sel: null,
        flavour: this.models.map(() => 0),
        pack: this.models.map(() => 0),
        added: false, detailsOpen: false, qty: 1, acc: 'what',
      };
      this.builtFor = -1;

      this.hydrateTiles();
      this.bind();
      this.enter();
      this.update();
    }

    $(sel) { return this.querySelector(sel); }
    bindText(name, text) { this.querySelectorAll('[data-bind="' + name + '"]').forEach((n) => { n.textContent = text; }); }

    // Fill tile copy/tint from the model (covers blocks with no product picked).
    hydrateTiles() {
      this.tiles.forEach((t, i) => {
        const m = this.models[i];
        const prod = t.querySelector('[data-tile-product]');
        const mom = t.querySelector('[data-tile-moment]');
        if (prod) prod.textContent = m.title;
        if (mom) mom.textContent = m.moment;
        t.style.setProperty('--tile-tint', m.tint);
        const img = t.querySelector('.op-shop__tile-img');
        if (img) img.setAttribute('aria-label', m.title);
      });
    }

    bind() {
      this.tiles.forEach((t, i) => t.addEventListener('click', () => this.toggle(i)));
      this.addEventListener('click', (e) => {
        const a = e.target.closest('[data-action],[data-flavour],[data-pack],[data-acc]');
        if (!a || !this.contains(a)) return;
        if (a.hasAttribute('data-flavour')) return this.setFlavour(Number(a.dataset.flavour));
        if (a.hasAttribute('data-pack')) return this.setPack(Number(a.dataset.pack));
        if (a.hasAttribute('data-acc')) { const k = a.dataset.acc; this.state.acc = this.state.acc === k ? null : k; return this.update(); }
        switch (a.dataset.action) {
          case 'close-panel': return this.closePanel();
          case 'open-details': return this.openDetails();
          case 'close-details': return this.closeDetails();
          case 'add': return this.L.marketplace ? this.buy() : this.add(1);
          case 'add-dialog': return this.L.marketplace ? this.buy() : this.add(Math.max(1, this.state.qty || 1));
          case 'qty-down': this.state.qty = Math.max(1, this.state.qty - 1); return this.update();
          case 'qty-up': this.state.qty = Math.min(20, this.state.qty + 1); return this.update();
        }
      });
      // Click on the dimmed backdrop (not the sheet) closes the dialog.
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeDetails(); });
      this.onKey = (e) => {
        if (e.key !== 'Escape') return;
        if (this.state.detailsOpen) this.closeDetails();
        else if (this.state.sel !== null) this.closePanel();
      };
      // Theme editor: selecting a block opens its tile.
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.tiles.indexOf(e.target);
        if (i > -1 && this.state.sel !== i) this.toggle(i);
      });
    }

    // Enter-in-view: fade/rise the header and tiles once, then switch tiles to hover timings.
    enter() {
      const root = this.section;
      if (this.reduce || !('IntersectionObserver' in window)) {
        root.classList.add('is-inview', 'is-entered');
        return;
      }
      // Jump to the hidden start state without animating out, then reveal on scroll.
      root.classList.add('is-armed', 'no-anim');
      void root.offsetWidth;
      root.classList.remove('no-anim');
      this.io = new IntersectionObserver(([e]) => {
        if (!e || !e.isIntersecting) return;
        root.classList.add('is-inview');
        this.io.disconnect();
        this.tEnter = setTimeout(() => root.classList.add('is-entered'), 900);
      }, { threshold: 0.15 });
      this.io.observe(root);
    }

    toggle(i) {
      clearTimeout(this.tAdded);
      if (this.state.sel === i) { if (!this.closing) this.closePanel(); return; }
      this.cancelClose();
      this.state.sel = i;
      this.state.added = false;
      this.update();
    }

    closePanel() {
      const p = this.panel;
      clearTimeout(this.ct);
      if (p.hidden || this.reduce) return this.finishClose();
      const gap = parseFloat(getComputedStyle(this.grid).rowGap) || 0;
      const padX = getComputedStyle(p).paddingLeft;
      this.closing = true;
      p.style.height = p.offsetHeight + 'px';
      p.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this.closing) return;
        p.style.height = '0px';
        p.style.opacity = '0';
        p.style.marginTop = -gap + 'px';
        p.style.transform = 'translateY(-6px)';
        p.style.padding = '0px ' + padX;
      }));
      this.ct = setTimeout(() => this.finishClose(), 400);
    }

    cancelClose() {
      clearTimeout(this.ct);
      this.closing = false;
      ['height', 'overflow', 'opacity', 'marginTop', 'transform', 'padding'].forEach((k) => { this.panel.style[k] = ''; });
    }

    finishClose() {
      this.cancelClose();
      const tile = this.tiles[this.state.sel];
      const hadFocus = this.panel.contains(document.activeElement);
      this.state.sel = null;
      this.state.added = false;
      this.update();
      if (hadFocus && tile) tile.focus({ preventScroll: true });
    }

    setFlavour(k) { const i = this.state.sel; if (i === null) return; this.state.flavour[i] = k; this.state.added = false; this.update(); }
    setPack(k) { const i = this.state.sel; if (i === null) return; this.state.pack[i] = k; this.state.added = false; this.update(); }

    openDetails() {
      if (this.state.sel === null) return;
      this.lastFocus = document.activeElement;
      Object.assign(this.state, { detailsOpen: true, qty: 1, acc: 'what' });
      this.update();
      const btn = this.dialog.querySelector('.op-shop__dialog-close');
      if (btn) btn.focus({ preventScroll: true });
    }

    closeDetails() {
      this.state.detailsOpen = false;
      this.update();
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    current() {
      const i = this.state.sel;
      const m = this.models[i];
      const fi = Math.min(this.state.flavour[i], m.flavours.length - 1);
      let pi = Math.min(this.state.pack[i], m.packs.length - 1);
      if (m.packs[pi].cells[fi] && m.packs[pi].cells[fi].na) {
        const k = m.packs.findIndex((p) => p.cells[fi] && !p.cells[fi].na);
        if (k > -1) this.state.pack[i] = pi = k;
      }
      return { i, m, fi, pi, pack: m.packs[pi], cell: m.packs[pi].cells[fi] || { price: 0, id: null } };
    }

    // Marketplace mode: open the buy dialog (assets/op-buy.js) for the chosen flavour and pack.
    buy() {
      if (this.state.sel === null || !window.OnePercentBuy) return;
      const { m, fi, pack, cell } = this.current();
      if (this.soon(cell)) return;
      window.OnePercentBuy.open(cell.id, {
        product: m.title,
        flavour: m.flavours[fi].name,
        pack: this.packLabel(m, pack.count),
        price: money(cell.price),
        image: m.flavours[fi].img,
      });
    }

    // Marketplace mode: an option with no listing on any store is not on sale yet.
    soon(cell) {
      const B = this.L.marketplace && window.ONEPERCENT_BUY;
      const v = B && cell && cell.id ? B.variants[String(cell.id)] : null;
      return !!(v && v.soon);
    }

    setBuyButton(btn, label, cell) {
      const soon = this.soon(cell);
      btn.textContent = soon ? window.ONEPERCENT_BUY.soonLabel || 'Coming soon' : label;
      btn.disabled = soon;
    }

    add(qty) {
      if (this.state.sel === null) return;
      const { m, fi, pack, cell } = this.current();
      const C = window.OnePercentCart;
      this.state.added = true;
      this.update();
      clearTimeout(this.tAdded);
      this.tAdded = setTimeout(() => { this.state.added = false; this.update(); }, 1800);
      if (!C) return;
      const key = C.key(m.range, m.flavours[fi].name, pack.count);
      const map = window.ONEPERCENT_VARIANTS;
      if (map && !map[key] && cell.id) map[key] = cell.id;
      const revert = () => { clearTimeout(this.tAdded); this.state.added = false; this.update(); };
      Promise.resolve(C.add(key, qty)).then((r) => { if (r && r.ok === false) revert(); }, revert);
    }

    // Rebuild the per-range lists (claims, pills, certs) when the selected range changes.
    build(i) {
      const m = this.models[i];
      const fill = (name, items, make) => this.querySelectorAll('[data-list="' + name + '"]').forEach((box) => {
        box.replaceChildren(...items.map(make));
      });
      const claim = (t) => el('span', 'op-shop__claim', t);
      fill('claims', m.claims, claim);
      fill('d-claims', m.claims, claim);
      const flavourPill = (fl, k) => {
        const b = el('button', 'op-shop__pill op-shop__pill--flavour');
        b.type = 'button';
        b.dataset.flavour = k;
        const th = el('span', 'op-shop__thumb');
        th.setAttribute('aria-hidden', 'true');
        if (fl.img) th.style.backgroundImage = cssUrl(fl.img);
        b.append(th, document.createTextNode(fl.name));
        return b;
      };
      fill('flavours', m.flavours, flavourPill);
      fill('d-flavours', m.flavours, flavourPill);
      const packPill = (pk, k) => {
        const b = el('button', 'op-shop__pill', this.packLabel(m, pk.count));
        b.type = 'button';
        b.dataset.pack = k;
        return b;
      };
      fill('packs', m.packs, packPill);
      fill('d-packs', m.packs, packPill);
      this.builtFor = i;
    }

    packLabel(m, n) {
      const L = this.L;
      return n === 1
        ? String(L.packSingle || 'Single [unit]').replace('[unit]', m.unit)
        : String(L.packMulti || '[count]-pack').replace('[count]', n);
    }

    update() {
      const s = this.state;
      const L = this.L;
      this.tiles.forEach((t, k) => {
        const open = s.sel === k;
        t.setAttribute('aria-expanded', String(open));
        t.setAttribute('aria-label', (open ? L.tileClose || 'Close' : L.tileShop || 'Shop') + ' ' + this.models[k].title);
      });
      if (s.sel === null) {
        this.panel.hidden = true;
        this.dialog.hidden = true;
        s.detailsOpen = false;
        return;
      }
      if (this.builtFor !== s.sel) this.build(s.sel);
      const { i, m, fi, pi, pack, cell } = this.current();
      const th = m.flavours[fi].colors;
      const flavourName = m.flavours[fi].name;

      this.style.setProperty('--sel', String(i));
      this.style.setProperty('--p-tint', th[0]);
      this.style.setProperty('--p-accent', th[1]);
      this.style.setProperty('--p-pill-bg', th[2]);
      this.style.setProperty('--p-pill-fg', th[3]);

      // Prices: saving is measured against the smallest pack's unit price, as in the design.
      const count = pack.count;
      const price = cell.price;
      const first = m.packs.find((p) => p.cells[fi] && !p.cells[fi].na) || m.packs[0];
      const unitPrice = (first.cells[fi] || first.cells[0]).price / (first.count || 1);
      const was = unitPrice * count;
      const saving = count > 1 && price < was;
      const saveText = (L.save || 'Save') + ' ' + Math.round((1 - price / was) * 100) + '%';
      const unit = count === 1
        ? '1 ' + m.unit + ' · ' + m.size
        : count + ' ' + m.unit + 's · ' + m.size + ' ' + (L.each || 'each');

      // Panel
      this.panel.setAttribute('aria-label', m.title);
      this.panel.hidden = false;
      this.bindText('beat', m.beat);
      this.bindText('story', m.story);
      this.bindText('d-story', m.story);
      this.querySelectorAll('[data-flavour]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.flavour) === fi)));
      this.querySelectorAll('[data-pack]').forEach((b) => {
        const c = m.packs[Number(b.dataset.pack)] && m.packs[Number(b.dataset.pack)].cells[fi];
        b.setAttribute('aria-pressed', String(Number(b.dataset.pack) === pi));
        b.hidden = !!(c && c.na);
      });
      this.bindText('price', money(price));
      const wasEl = this.$('[data-bind="was"]');
      wasEl.textContent = money(was);
      wasEl.hidden = !saving;
      const saveEl = this.$('[data-bind="save"]');
      saveEl.textContent = saveText;
      saveEl.hidden = !saving;
      this.bindText('unit', unit);
      this.setBuyButton(this.$('[data-action="add"]'), s.added ? L.added || 'Added ✓' : L.add || 'Add to cart', cell);

      // Dialog
      this.dialog.hidden = !s.detailsOpen;
      if (!s.detailsOpen) return;
      const lab = m.label;
      const lf = lab.flavours[fi] || { kcal: '', rows: [], ing: '' };
      const mul = scaler(lab.mult);
      const ov = lab.override || {};
      const title = m.title + ' — ' + flavourName;
      const cut = this.$('[data-bind="cutout"]');
      cut.style.backgroundImage = m.flavours[fi].img ? cssUrl(m.flavours[fi].img) : '';
      cut.setAttribute('aria-label', title);
      this.bindText('overline', 'one% ' + m.title + ' · ' + m.size);
      this.bindText('title', title);
      this.bindText('d-price', money(price));
      this.bindText('d-unit', unit);
      const dSave = this.$('[data-bind="d-save"]');
      dSave.textContent = saveText;
      dSave.hidden = !saving;
      this.bindText('qty', String(s.qty));
      this.setBuyButton(this.$('[data-action="add-dialog"]'), s.added
        ? L.added || 'Added ✓'
        : L.marketplace ? (L.add || 'Buy now') : (L.add || 'Add to cart') + ' · ' + money(price * s.qty), cell);

      this.querySelectorAll('[data-acc]').forEach((b) => b.setAttribute('aria-expanded', String(s.acc === b.dataset.acc)));
      this.querySelectorAll('[data-acc-body]').forEach((b) => { b.hidden = s.acc !== b.dataset.accBody; });
      this.bindText('what', lab.what);
      this.bindText('what-head', lab.whatHead);
      this.bindText('what-body', lab.whatBody);
      this.bindText('serving', lab.serving);
      this.bindText('kcal', mul(lf.kcal));
      const rows = this.$('[data-list="rows"]');
      rows.replaceChildren(...(lf.rows || []).map((r) => {
        const row = Array.isArray(r) ? r : [r.k || r.label, r.v || r.value, r.dv, r.bold, r.indent];
        const o = ov[row[0]];
        const div = el('div', 'op-shop__row' + (row[3] ? ' op-shop__row--bold' : row[4] ? ' op-shop__row--sub' : ''));
        div.append(el('span', '', row[0]), el('span', '', o ? o[0] : mul(row[1])), el('span', '', o ? o[1] : mul(row[2]) || ''));
        return div;
      }));
      this.bindText('ingredients', lf.ing);
      this.bindText('allergens', lab.allergens);
      const note = this.$('[data-bind="note"]');
      note.textContent = lab.note;
      note.hidden = !lab.note;
      this.bindText('storage', m.storage || L.storage || '');
      this.$('[data-list="certs"]').replaceChildren(...(lab.certs || []).map((c) => {
        const k = Array.isArray(c) ? c[0] : c.title;
        const v = Array.isArray(c) ? c[1] : c.detail;
        const chip = el('span', 'op-shop__cert');
        if (v) chip.title = v;
        const tick = el('span', 'op-shop__cert-tick', '✓');
        tick.setAttribute('aria-hidden', 'true');
        chip.append(tick, document.createTextNode(k));
        return chip;
      }));
    }
  }

  customElements.define('op-shop', OpShop);
})();
